import { useMemo, useState } from 'react';
import { colors } from '../../theme';
import { formatCount, formatInr } from '../../utils/formatInr';
import styles from './Analytics.module.css';

function niceMax(v) {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / pow) * pow;
}

/** Net revenue: always use kpis.net_revenue (can be negative). Do not recompute. */
export function WaterfallChart({ kpis }) {
  if (!kpis) return null;

  const revenue = Math.max(0, Number(kpis.revenue) || 0);
  const discounts = Math.abs(kpis.discounts || 0);
  const deliveryFees = Math.abs(kpis.delivery_fees || 0);
  const returnsCredited = Math.abs(kpis.returns_credited || 0);
  const net =
    kpis.net_revenue != null && !Number.isNaN(Number(kpis.net_revenue))
      ? Number(kpis.net_revenue)
      : null;

  const keptPct = revenue > 0 && net != null ? (net / revenue) * 100 : null;
  const returnsPct = revenue > 0 ? (returnsCredited / revenue) * 100 : 0;
  const scale = Math.max(revenue, returnsCredited, Math.abs(net ?? 0), 1);
  // Bar width uses non-negative amount; headline shows true (possibly negative) net.
  const netBar = Math.max(0, net ?? 0);

  // Stacked bar v1: kept + returns. Discounts / delivery fees are side notes only — not subtracted.
  const stackSegments = [
    { key: 'kept', label: 'Net revenue', amount: netBar, color: '#2E7D32', className: styles.netSegKept },
    {
      key: 'returns',
      label: 'Returns credited',
      amount: returnsCredited,
      color: '#F5C6CB',
      className: styles.netSegReturns,
    },
  ].filter((s) => s.amount > 0 || (s.key === 'kept' && net != null && net >= 0));

  const sideMetrics = [
    { key: 'discounts', label: 'Discounts (already in list)', amount: discounts, color: '#E8A317' },
    { key: 'fees', label: 'Delivery fees (income)', amount: deliveryFees, color: '#E57373' },
  ].filter((d) => d.amount > 0);

  const maxSide = Math.max(1, ...sideMetrics.map((d) => d.amount), returnsCredited);

  return (
    <div className={styles.netCompose}>
      <div className={styles.netComposeHead}>
        <div>
          <p className={styles.netComposeValue}>{formatInr(net)}</p>
        </div>
        <div className={styles.netComposeHeadRight}>
          <p className={styles.netComposeGross}>of {formatInr(revenue)} gross</p>
          {keptPct != null && (
            <span className={styles.netComposeKeptBadge}>{keptPct.toFixed(1)}% after returns</span>
          )}
        </div>
      </div>

      <div
        className={styles.netStackBar}
        role="img"
        aria-label={`Net ${formatInr(net)} of ${formatInr(revenue)} gross after returns`}
      >
        {stackSegments.map((s) => (
          <div
            key={s.key}
            className={`${styles.netStackSeg} ${s.className}`}
            style={{ width: `${Math.max(s.amount > 0 ? 1.5 : 0, (s.amount / scale) * 100)}%` }}
            title={`${s.label}: ${formatInr(s.amount, { compact: false })}`}
          />
        ))}
      </div>

      <div className={styles.netStackLegend}>
        {stackSegments.map((s) => (
          <span key={s.key} className={styles.netStackLegendItem}>
            <i style={{ background: s.color }} />
            {s.key === 'kept'
              ? `Net revenue ${formatInr(net)}`
              : `Returns ${formatInr(s.amount)} · ${returnsPct.toFixed(1)}%`}
          </span>
        ))}
      </div>

      {(returnsCredited > 0 || sideMetrics.length > 0) && (
        <div className={styles.netDeductBlock}>
          <div className={styles.netDeductHead}>
            <span>Composition notes</span>
            <span>Not a second P&amp;L</span>
          </div>
          <ul className={styles.netDeductList}>
            {returnsCredited > 0 && (
              <li className={styles.netDeductRow}>
                <span className={styles.netDeductLabel}>Returns credited</span>
                <div className={styles.netDeductBarTrack}>
                  <div
                    className={styles.netDeductBarFill}
                    style={{
                      width: `${(returnsCredited / maxSide) * 100}%`,
                      background: '#F5C6CB',
                    }}
                  />
                </div>
                <span className={styles.netDeductAmount}>
                  {formatInr(returnsCredited, { compact: false })} · {returnsPct.toFixed(1)}%
                </span>
              </li>
            )}
            {sideMetrics.map((d) => {
              const pct = revenue > 0 ? (d.amount / revenue) * 100 : 0;
              return (
                <li key={d.key} className={styles.netDeductRow}>
                  <span className={styles.netDeductLabel}>{d.label}</span>
                  <div className={styles.netDeductBarTrack}>
                    <div
                      className={styles.netDeductBarFill}
                      style={{
                        width: `${(d.amount / maxSide) * 100}%`,
                        background: d.color,
                      }}
                    />
                  </div>
                  <span className={styles.netDeductAmount}>
                    {formatInr(d.amount, { compact: false })} · {pct.toFixed(1)}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className={styles.captionLine}>
        Cash collected (POS tenders + delivery COD) {formatInr(kpis.cash_collected)} · store credit
        used {formatInr(kpis.store_credit_used)}
      </p>
    </div>
  );
}

export function TrendChart({
  buckets = [],
  channel,
  metric,
  compare,
  xAxisLabel = 'Date',
  yAxisLabel = 'Value',
}) {
  const [hover, setHover] = useState(null);
  const w = 640;
  const h = 176;
  const pad = { t: 12, r: 16, b: 44, l: 58 };

  const seriesKeys = useMemo(() => {
    if (channel === 'pos') return ['pos'];
    if (channel === 'delivery') return ['delivery'];
    return ['delivery', 'pos'];
  }, [channel]);

  const { yMax, paths } = useMemo(() => {
    const vals = [];
    buckets.forEach((b) => {
      seriesKeys.forEach((k) => {
        if (typeof b[k] === 'number') vals.push(b[k]);
      });
      if (compare && typeof b.previous === 'number') vals.push(b.previous);
    });
    const max = niceMax(Math.max(0, ...vals));
    const innerW = w - pad.l - pad.r;
    const innerH = h - pad.t - pad.b;
    const n = Math.max(buckets.length - 1, 1);

    const toPts = (key) =>
      buckets.map((b, i) => {
        const x = pad.l + (i / n) * innerW;
        const raw = b[key];
        if (typeof raw !== 'number') {
          return { x, y: null, value: null, label: b.label };
        }
        const y = pad.t + innerH - (raw / max) * innerH;
        return { x, y, value: raw, label: b.label };
      });

    // Break path at missing buckets (previous[] may be shorter than series[]).
    const toPath = (pts) => {
      let d = '';
      let drawing = false;
      pts.forEach((p) => {
        if (p.value == null || p.y == null) {
          drawing = false;
          return;
        }
        d += `${drawing ? 'L' : 'M'} ${p.x.toFixed(1)} ${p.y.toFixed(1)} `;
        drawing = true;
      });
      return d.trim();
    };

    const built = {};
    seriesKeys.forEach((k) => {
      built[k] = toPts(k);
    });
    if (compare) built.previous = toPts('previous');

    return { yMax: max, paths: Object.fromEntries(Object.entries(built).map(([k, pts]) => [k, toPath(pts)])) };
  }, [buckets, seriesKeys, compare, pad.b, pad.l, pad.r, pad.t]);

  const fmtY = (v) => (metric === 'orders' ? formatCount(v) : formatInr(v, { digits: 1 }));

  const colorFor = (key) => {
    if (key === 'pos') return '#7C3AED';
    if (key === 'delivery') return colors.primaryDark;
    return colors.textSecondary;
  };

  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${w} ${h}`} className={`${styles.chartSvg} ${styles.chartSvgTrend}`} role="img" aria-label="Trend chart">
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = pad.t + (h - pad.t - pad.b) * (1 - t);
          const val = yMax * t;
          return (
            <g key={t}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="var(--color-border-soft)" strokeWidth="1" />
              <text x={pad.l - 8} y={y + 3} textAnchor="end" className={styles.axisText}>
                {fmtY(val)}
              </text>
            </g>
          );
        })}
        {buckets.map((b, i) => {
          if (buckets.length > 14 && i % Math.ceil(buckets.length / 8) !== 0 && i !== buckets.length - 1) return null;
          const n = Math.max(buckets.length - 1, 1);
          const x = pad.l + (i / n) * (w - pad.l - pad.r);
          return (
            <text key={b.label + i} x={x} y={h - 22} textAnchor="middle" className={styles.axisText}>
              {b.label}
            </text>
          );
        })}
        <text
          x={pad.l + (w - pad.l - pad.r) / 2}
          y={h - 6}
          textAnchor="middle"
          className={styles.axisTitle}
        >
          {xAxisLabel}
        </text>
        <text
          x={14}
          y={pad.t + (h - pad.t - pad.b) / 2}
          textAnchor="middle"
          className={styles.axisTitle}
          transform={`rotate(-90 14 ${pad.t + (h - pad.t - pad.b) / 2})`}
        >
          {yAxisLabel}
        </text>
        {compare && paths.previous && (
          <path d={paths.previous} fill="none" stroke={colors.textSecondary} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.7" />
        )}
        {seriesKeys.map((key) => (
          <path
            key={key}
            d={paths[key]}
            fill="none"
            stroke={colorFor(key)}
            strokeWidth="2.25"
            strokeDasharray={key === 'pos' && seriesKeys.length > 1 ? '6 4' : undefined}
          />
        ))}
        {buckets.map((b, i) => {
          const n = Math.max(buckets.length - 1, 1);
          const x = pad.l + (i / n) * (w - pad.l - pad.r);
          return (
            <rect
              key={`hit-${i}`}
              x={x - 10}
              y={pad.t}
              width={20}
              height={h - pad.t - pad.b}
              fill="transparent"
              onMouseEnter={() => setHover({ i, x, bucket: b })}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
        {hover && (
          <line
            x1={hover.x}
            x2={hover.x}
            y1={pad.t}
            y2={h - pad.b}
            stroke={colors.border}
            strokeDasharray="3 3"
          />
        )}
      </svg>
      {hover && (
        <div className={styles.tooltip} style={{ left: `${(hover.x / w) * 100}%` }}>
          <strong>{hover.bucket.label}</strong>
          {seriesKeys.map((k) => (
            <div key={k}>
              {k === 'pos' ? 'POS' : 'Delivery'}: {fmtY(hover.bucket[k] || 0)}
            </div>
          ))}
          {compare && typeof hover.bucket.previous === 'number' && (
            <div>Previous: {fmtY(hover.bucket.previous)}</div>
          )}
        </div>
      )}
      <div className={styles.legendRow}>
        {seriesKeys.map((k) => (
          <span key={k} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{
                background: colorFor(k),
                borderStyle: k === 'pos' && seriesKeys.length > 1 ? 'dashed' : 'solid',
              }}
            />
            {k === 'pos' ? 'POS' : 'Delivery'}
          </span>
        ))}
        {compare && (
          <span className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: colors.textSecondary, opacity: 0.5 }} />
            Previous period
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Generic 2-series line chart.
 * @param {Array} buckets - [{ label, [keyA], [keyB] }, ...]
 * @param {Array<{ key: string, label: string, color: string, dashed?: boolean }>} series
 * @param {'count'|'money'} valueType
 */
export function DualLineChart({
  buckets = [],
  series = [],
  valueType = 'count',
  ariaLabel = 'Line chart',
  xAxisLabel = 'Date',
  yAxisLabel = 'Count',
}) {
  const [hover, setHover] = useState(null);
  const w = 640;
  const h = 236;
  const pad = { t: 16, r: 16, b: 46, l: 58 };
  const seriesKeys = series.map((s) => s.key);

  const { yMax, paths } = useMemo(() => {
    const vals = [];
    buckets.forEach((b) => {
      seriesKeys.forEach((k) => {
        if (typeof b[k] === 'number') vals.push(b[k]);
      });
    });
    const max = niceMax(Math.max(0, ...vals));
    const innerW = w - pad.l - pad.r;
    const innerH = h - pad.t - pad.b;
    const n = Math.max(buckets.length - 1, 1);

    const toPts = (key) =>
      buckets.map((b, i) => ({
        x: pad.l + (i / n) * innerW,
        y: pad.t + innerH - ((b[key] || 0) / max) * innerH,
      }));

    const toPath = (pts) =>
      pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

    const built = {};
    seriesKeys.forEach((k) => {
      built[k] = toPath(toPts(k));
    });
    return { yMax: max, paths: built };
  }, [buckets, seriesKeys, pad.b, pad.l, pad.r, pad.t]);

  const fmtY = (v) => (valueType === 'money' ? formatInr(v, { digits: 1 }) : formatCount(v));
  const colorMap = Object.fromEntries(series.map((s) => [s.key, s.color]));
  const labelMap = Object.fromEntries(series.map((s) => [s.key, s.label]));
  const dashedMap = Object.fromEntries(series.map((s) => [s.key, !!s.dashed]));

  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${w} ${h}`} className={styles.chartSvg} role="img" aria-label={ariaLabel}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = pad.t + (h - pad.t - pad.b) * (1 - t);
          return (
            <g key={t}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="var(--color-border-soft)" strokeWidth="1" />
              <text x={pad.l - 8} y={y + 3} textAnchor="end" className={styles.axisText}>
                {fmtY(yMax * t)}
              </text>
            </g>
          );
        })}
        {buckets.map((b, i) => {
          if (buckets.length > 14 && i % Math.ceil(buckets.length / 8) !== 0 && i !== buckets.length - 1) {
            return null;
          }
          const n = Math.max(buckets.length - 1, 1);
          const x = pad.l + (i / n) * (w - pad.l - pad.r);
          return (
            <text key={`${b.label}-${i}`} x={x} y={h - 22} textAnchor="middle" className={styles.axisText}>
              {b.label}
            </text>
          );
        })}
        <text
          x={pad.l + (w - pad.l - pad.r) / 2}
          y={h - 6}
          textAnchor="middle"
          className={styles.axisTitle}
        >
          {xAxisLabel}
        </text>
        <text
          x={14}
          y={pad.t + (h - pad.t - pad.b) / 2}
          textAnchor="middle"
          className={styles.axisTitle}
          transform={`rotate(-90 14 ${pad.t + (h - pad.t - pad.b) / 2})`}
        >
          {yAxisLabel}
        </text>
        {seriesKeys.map((key) => (
          <path
            key={key}
            d={paths[key]}
            fill="none"
            stroke={colorMap[key]}
            strokeWidth="2.25"
            strokeDasharray={dashedMap[key] ? '6 4' : undefined}
          />
        ))}
        {buckets.map((b, i) => {
          const n = Math.max(buckets.length - 1, 1);
          const x = pad.l + (i / n) * (w - pad.l - pad.r);
          return (
            <rect
              key={`hit-${i}`}
              x={x - 10}
              y={pad.t}
              width={20}
              height={h - pad.t - pad.b}
              fill="transparent"
              onMouseEnter={() => setHover({ x, bucket: b })}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
        {hover && (
          <line
            x1={hover.x}
            x2={hover.x}
            y1={pad.t}
            y2={h - pad.b}
            stroke={colors.border}
            strokeDasharray="3 3"
          />
        )}
      </svg>
      {hover && (
        <div className={styles.tooltip} style={{ left: `${(hover.x / w) * 100}%` }}>
          <strong>{hover.bucket.label}</strong>
          {seriesKeys.map((k) => (
            <div key={k}>
              {labelMap[k]}: {fmtY(hover.bucket[k] || 0)}
            </div>
          ))}
        </div>
      )}
      <div className={styles.legendRow}>
        {series.map((s) => (
          <span key={s.key} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{
                background: s.color,
                borderStyle: s.dashed ? 'dashed' : 'solid',
              }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ChannelDonut({ channels = [], totalRevenue }) {
  const total =
    totalRevenue != null
      ? Number(totalRevenue) || 1
      : channels.reduce((s, c) => s + (c.revenue || 0), 0) || 1;
  const colorsMap = { delivery: colors.primaryDark, pos: '#7C3AED' };
  let angle = -90;
  const slices = channels.map((c) => {
    const ch = String(c.channel || '').toLowerCase();
    const pct =
      c.share_pct != null ? Number(c.share_pct) / 100 : (c.revenue || 0) / total;
    const sweep = pct * 360;
    const start = angle;
    angle += sweep;
    return { ...c, channel: ch, pct, start, sweep, color: colorsMap[ch] || colors.primary };
  });

  const r = 54;
  const cx = 70;
  const cy = 70;
  const toXy = (deg, radius = r) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };

  const arc = (start, sweep) => {
    if (sweep >= 359.9) {
      return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`;
    }
    const [x1, y1] = toXy(start);
    const [x2, y2] = toXy(start + sweep);
    const large = sweep > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
  };

  const channelTitle = (ch) => (ch === 'pos' ? 'POS' : 'Delivery');

  return (
    <div className={styles.channelMix}>
      <div className={styles.channelVisual}>
        <svg viewBox="0 0 140 140" className={styles.donutSvg} role="img" aria-label="Revenue share by channel">
          {slices.map((s) => (
            <path key={s.channel} d={arc(s.start, s.sweep)} fill={s.color} opacity="0.92" />
          ))}
          <circle cx={cx} cy={cy} r={34} fill="var(--color-background)" />
          <text x={cx} y={cy - 2} textAnchor="middle" className={styles.donutCenterValue}>
            {formatInr(total)}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className={styles.donutCenterLabel}>
            Total
          </text>
        </svg>

        <ul className={styles.channelShareList}>
          {slices.map((s) => (
            <li key={s.channel} className={styles.channelShareItem}>
              <div className={styles.channelShareHead}>
                <span className={styles.legendSwatch} style={{ background: s.color, height: 8, width: 8, borderRadius: 2 }} />
                <strong>{channelTitle(s.channel)}</strong>
                <span className={styles.channelSharePct}>{(s.pct * 100).toFixed(0)}%</span>
              </div>
              <div className={styles.channelShareBarTrack}>
                <div
                  className={styles.channelShareBarFill}
                  style={{ width: `${s.pct * 100}%`, background: s.color }}
                />
              </div>
              <p className={styles.channelShareMeta}>
                {formatInr(s.revenue)} · {formatCount(s.orders)} orders
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.channelMetricGrid}>
        {channels.map((c) => {
          const ch = String(c.channel || '').toLowerCase();
          return (
            <div key={c.channel} className={styles.channelMetricCard}>
              <span className={styles.channelMetricLabel}>
                <span
                  className={styles.legendSwatch}
                  style={{ background: colorsMap[ch] || colors.primary }}
                />
                {channelTitle(ch)} AOV
              </span>
              <strong className={styles.channelMetricValue}>
                {c.aov == null ? '—' : formatInr(c.aov, { compact: false })}
              </strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PAYMENT_LABELS = {
  UPI: 'UPI',
  CASH: 'Cash',
  CARD: 'Card',
  COD: 'COD',
  CREDIT: 'Store credit',
};

/** Payments: collected only. Prefer collected_total; Card / Store credit labels. */
export function PaymentsBars({ payments = [], collectedTotal }) {
  const rows = payments.filter((p) => (p.collected || 0) > 0);
  const totalCollected = collectedTotal != null ? Number(collectedTotal) : 0;
  const max = Math.max(1, ...rows.map((p) => p.collected || 0));

  return (
    <div>
      <p className={styles.captionLine}>Collected {formatInr(totalCollected)}</p>
      <div className={styles.hBars}>
        {rows.map((p) => {
          const collectedPct = ((p.collected || 0) / max) * 100;
          const methodKey = String(p.method || '').toUpperCase();
          const label = PAYMENT_LABELS[methodKey] || p.method;
          return (
            <div key={p.method} className={styles.hBarRow}>
              <span className={styles.hBarLabel}>{label}</span>
              <div className={styles.hBarTrack}>
                <div
                  className={styles.hBarCollected}
                  style={{ width: `${collectedPct}%` }}
                  title={`Collected ${formatInr(p.collected, { compact: false })}`}
                />
              </div>
              <span className={styles.hBarValue}>{formatInr(p.collected)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CategoriesBars({ categories = [], metric = 'revenue', isAdmin }) {
  const valueOf = (c) => {
    if (metric === 'qty') return c.qty;
    if (metric === 'profit') return c.gross_profit;
    return c.revenue;
  };
  const max = Math.max(1, ...categories.map(valueOf));
  const fmt = (c) => (metric === 'qty' ? formatCount(valueOf(c)) : formatInr(valueOf(c)));

  return (
    <div className={styles.hBars}>
      {categories.map((c) => (
        <div key={c.category} className={styles.hBarRow}>
          <span className={styles.hBarLabelWide}>{c.category}</span>
          <div className={styles.hBarTrack}>
            <div
              className={styles.hBarCollected}
              style={{ width: `${(valueOf(c) / max) * 100}%` }}
            />
          </div>
          <span className={styles.hBarValue}>
            {fmt(c)}
            {isAdmin && c.margin_pct != null && (
              <span className={styles.mutedInline}> · {c.margin_pct.toFixed(1)}%</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
